import {PostLoginResponseData} from '$api/bookingFlow';
import {Updater} from 'use-immer';
import {PatientCarerDetailsValidatedFormData} from './components/PatientCarerDetailForm/schema';
import {PatientContactDetailsFormData} from './components/PatientContactDetailForm/schema';

import {GPOrOpticianReferralValidatedFormData} from './components/GPOrOpticianReferralForm/schema';
import {Slot} from '$modules/frontdesk/type';
import {PatientInsuranceDetailFormData} from './components/PatientInsuranceDetailForm/schema';
export enum BookingView {
  TransitionView,
  AppointmentSelectionView,
  CareSelectionView,
  SubcareSelectionView,
  ConsultantSelectionView,
  DateSelectionView,
  ChoicesConfirmationView,
  RegisterWithStoneygateView,
  PatientCarerDetailsView,
  GPOrOpticianReferralView,
  PatientContactDetailView,
  PatientInsuranceDetailView,
  LastStepView,
  AppSummaryView,

  CancelConfirmationView,
  CancelFormView,
  ReviewCancelSummary,

  PaymentFailureView,
  PayPalReturn,
}

export enum BookingStepperStage {
  Appointment,
  PatientDetails,
  Insurance,
}

export enum BookingPreferredTime {
  All = 'All time',
  Morning = 'Morning',
  Afternoon = 'Afternoon',
  Evening = 'Evening',
}

export interface Appointment {
  id: number;
  title: string;
  description: string;
  image: string;
  enable?: boolean;
  displayName?: string;
  isActive: boolean;
  isEnable: boolean;
}

export interface Care {
  id: number;
  title: string;
  description: string;
  image: string;
  enable?: boolean;
  isActive: boolean;
  isEnable: boolean;
}

export interface Subcare {
  id: number;
  title: string;
  description: string;
  enable?: boolean;
  isActive: boolean;
  isEnable: boolean;
}

export interface Consultant {
  id: number;
  profileUrl: string | null;
  fullName: string;
  doctorDetails: {
    bioLink: string;
    consultationFee: number;
    followupFee: number;
    specializations: {
      id: number;
      title: string;
    }[];
    description: string;
  } | null;
  priceLists: {
    consultationFee: number;
    followupFee: number;
  } | null;
}

export interface Relationship {
  id: number;
  name: string;
}

export interface PaymentMode {
  id: number;
  name: string;
}

export interface Payment {
  provider: string;
  consultant_id: number;
  payment_method_id: string;
  patient_id: number;
}

export interface PaymentCard {
  id: number;
  name: string;
}

export interface Insurance {
  id: number;
  name: string;
}

export interface City {
  code: string;
  name: string;
}

export interface Country {
  code: string;
  name: string;
  countryCode: string;
}

export interface BookingData {
  appointment?: Appointment;
  care?: Care;
  subcare?: Subcare;
  consultant?: Consultant;
  date?: Date;
  time?: {id: BookingPreferredTime; title: string};
  slot?: Slot;
  registered?: {
    isRegistered: boolean;
    email?: string;
    patientId?: number;
    patientMobileNumber?: string;
    isEmailvalidated?: boolean;
  };
  patientCarerDetails?: PatientCarerDetailsValidatedFormData;
  gpOrOpticianReferral?: GPOrOpticianReferralValidatedFormData;
  patientContactDetail?: PatientContactDetailsFormData;
  patientInsuranceDetail?: PatientInsuranceDetailFormData;
  password?: string;
  appointmentId?: number;
  payment_id?: number;

  cancelReasons?:
    | {
        reasonId: number;
        reasonName: string;
        otherReason?: string;
      }
    | {
        consult: boolean;
        reschedule: boolean;
        provider: boolean;
        other: boolean;
        otherReason?: string;
      };

  consultationAmount?: number;
  payment?: {
    amount: number;
    payment_id: string;
    payment_intent_id?: string;
    status?: string;
  };
  insurance_claim?: {
    insurance_id: number | undefined;
    membership_no: string | undefined;
    pre_authorisation_code: string;
  };
}

export type BookingDataUpdater = Updater<BookingData>;

export interface BookingChoices {
  appointment?: Appointment;
  care?: Care;
  subcare?: Subcare;
  consultant?: Consultant;
  date?: Date;
  time?: {id: string; title: string};
  registered?: {isRegistered: boolean; email?: string};
  patientCarerDetails?: PatientCarerDetailsValidatedFormData;
  gpOrOpticianReferral?: GPOrOpticianReferralValidatedFormData;
  patientContactDetail?: PatientContactDetailsFormData;
  patientInsuranceDetail?: PatientInsuranceDetailFormData;

  insurance?: Insurance;
  lastStep?: {password: string};
  loginData?: PostLoginResponseData;
  createAppointment?: {id: number; addressId: number; patientId: number};
}

export type AppointmentSelectionViewChoices = BookingData;

export type CareSelectionViewChoices = BookingData &
  Required<Pick<BookingData, 'appointment'>>;

export type SubcareSelectionViewChoices = BookingData &
  Required<Pick<BookingData, 'appointment' | 'care'>>;

export type ConsultantSelectionViewChoices = BookingData &
  Required<Pick<BookingData, 'appointment' | 'care' | 'subcare'>>;

export type DateSelectionViewChoices = BookingData &
  Required<
    Pick<BookingData, 'appointment' | 'care' | 'subcare' | 'consultant'>
  >;

export type ChoicesConfirmationViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      'appointment' | 'care' | 'subcare' | 'consultant' | 'date' | 'slot'
    >
  >;
export type CancelAppointmentChoices = Required<
  Pick<
    BookingData,
    | 'appointment'
    | 'care'
    | 'subcare'
    | 'consultant'
    | 'date'
    | 'slot'
    | 'appointmentId'
    | 'cancelReasons'
    | 'consultationAmount'
    | 'payment_id'
  >
>;

export type RegisterWithStoneygateViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      'appointment' | 'care' | 'subcare' | 'consultant' | 'date' | 'slot'
    >
  >;

export type PatientCarerDetailsViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      | 'appointment'
      | 'care'
      | 'subcare'
      | 'consultant'
      | 'date'
      | 'slot'
      | 'registered'
    >
  >;

export type GPOrOpticianReferralViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      | 'appointment'
      | 'care'
      | 'subcare'
      | 'consultant'
      | 'date'
      | 'slot'
      | 'registered'
      | 'patientCarerDetails'
    >
  >;

export type PatientContactDetailViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      | 'appointment'
      | 'care'
      | 'subcare'
      | 'consultant'
      | 'date'
      | 'slot'
      | 'registered'
      | 'patientCarerDetails'
      | 'gpOrOpticianReferral'
    >
  >;

export type PatientInsuranceDetailViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      | 'appointment'
      | 'care'
      | 'subcare'
      | 'consultant'
      | 'date'
      | 'slot'
      | 'registered'
      | 'patientCarerDetails'
      | 'gpOrOpticianReferral'
      | 'patientContactDetail'
    >
  >;

export type LastStepViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      | 'appointment'
      | 'care'
      | 'subcare'
      | 'consultant'
      | 'date'
      | 'slot'
      | 'registered'
      | 'patientCarerDetails'
      | 'gpOrOpticianReferral'
      | 'patientContactDetail'
      | 'patientInsuranceDetail'
    >
  >;

export type AppSummaryViewChoices = BookingData &
  Required<
    Pick<
      BookingData,
      | 'appointment'
      | 'care'
      | 'subcare'
      | 'consultant'
      | 'date'
      | 'slot'
      | 'registered'
      | 'patientCarerDetails'
      | 'gpOrOpticianReferral'
      | 'patientContactDetail'
      | 'patientInsuranceDetail'
      | 'password'
      | 'appointmentId'
      | 'payment'
    >
  >;

export enum BookingChoicesActionType {
  setBookingAppointmentCare = 'SET_BOOKING_APPOINTMENT_CARE',
  setAppointment = 'SET_APPOINTMENT',
  unsetAppointment = 'UNSET_APPOINTMENT',
  setCare = 'SET_CARE',
  unsetCare = 'UNSET_CARE',
  setSubcare = 'SET_SUBCARE',
  unsetSubcare = 'UNSET_SUBCARE',
  setConsultant = 'SET_CONSULTANT',
  unsetConsultant = 'UNSET_CONSULTANT',
  setDate = 'SET_DATE',
  unsetDate = 'UNSET_DATE',
  setTime = 'SET_TIME',
  unsetTime = 'UNSET_TIME',
  setRegistered = 'SET_REGISTERED',
  unsetRegistered = 'UNSET_REGISTERED',
  setPatientCarerDetails = 'SET_PATIENT_CARER_DETAILS',
  unsetPatientCarerDetails = 'UNSET_PATIENT_CARER_DETAILS',
  setGPOrOpticianReferral = 'SET_GP_OR_OPTICIAN_REFERRAL',
  unsetGPOrOpticianReferral = 'UNSET_GP_OR_OPTICIAN_REFERRAL',
  setPatientContactDetail = 'SET_CONTACT_DETAIL',
  unsetPatientContactDetail = 'UNSET_CONTACT_DETAIL',
  setPatientInsuranceDetails = 'SET_PATIENT_INSURANCE_DETAILS',
  unsetPatientInsuranceDetails = 'UNSET_PATIENT_INSURANCE_DETAILS',
  setLastStep = 'SET_LAST_STEP',
  unsetLastStep = 'UNSET_LAST_STEP',
  setLoginData = 'SET_LOGIN_DATA',
  unsetLoginData = 'UNSET_LOGIN_DATA',
  setCreateAppointment = 'SET_CREATE_APPOINTMENT',
  reset = 'RESET',
}

export type BookingChoicesAction =
  | {type: BookingChoicesActionType.setAppointment; payload: Appointment}
  | {type: BookingChoicesActionType.unsetAppointment}
  | {type: BookingChoicesActionType.setCare; payload: Care}
  | {type: BookingChoicesActionType.unsetCare}
  | {type: BookingChoicesActionType.setSubcare; payload: Subcare}
  | {type: BookingChoicesActionType.unsetSubcare}
  | {type: BookingChoicesActionType.setConsultant; payload: Consultant}
  | {type: BookingChoicesActionType.unsetConsultant}
  | {
      type: BookingChoicesActionType.setBookingAppointmentCare;
      payload: {appointment: Appointment; care: Care; subcare: Subcare};
    }
  | {type: BookingChoicesActionType.setDate; payload: Date}
  | {type: BookingChoicesActionType.unsetDate}
  | {
      type: BookingChoicesActionType.setTime;
      payload: {id: string; title: string};
    }
  | {type: BookingChoicesActionType.unsetTime}
  | {
      type: BookingChoicesActionType.setRegistered;
      payload: {isRegistered: boolean; email?: string};
    }
  | {type: BookingChoicesActionType.unsetRegistered}
  | {
      type: BookingChoicesActionType.setPatientCarerDetails;
      payload: PatientCarerDetailsValidatedFormData;
    }
  | {type: BookingChoicesActionType.unsetPatientCarerDetails}
  | {
      type: BookingChoicesActionType.setGPOrOpticianReferral;
      payload: GPOrOpticianReferralValidatedFormData;
    }
  | {type: BookingChoicesActionType.unsetGPOrOpticianReferral}
  | {
      type: BookingChoicesActionType.setPatientContactDetail;
      payload: PatientContactDetailsFormData;
    }
  | {type: BookingChoicesActionType.unsetPatientContactDetail}
  | {
      type: BookingChoicesActionType.setPatientInsuranceDetails;
      payload: PatientInsuranceDetailFormData;
    }
  | {
      type: BookingChoicesActionType.unsetPatientInsuranceDetails;
    }
  | {type: BookingChoicesActionType.setLastStep; payload: {password: string}}
  | {type: BookingChoicesActionType.unsetLastStep}
  | {
      type: BookingChoicesActionType.setLoginData;
      payload: PostLoginResponseData;
    }
  | {type: BookingChoicesActionType.unsetLoginData}
  | {
      type: BookingChoicesActionType.setCreateAppointment;
      payload: {id: number; addressId: number; patientId: number};
    }
  | {type: BookingChoicesActionType.reset};

export interface AppointmentDetailNavigatePayload {
  careType: string;
  date: Date | string;
}

export interface CancellationReasonWithId {
  reasonId: number;
  reasonName: string;
  otherReason?: string;
}
